// src/pages/user/QuotesPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  FileText, Clock, CheckCircle, XCircle, Send,
  ThumbsUp, ThumbsDown, AlertCircle, Download,
  Calendar, RefreshCw, ExternalLink, Building, Info,
} from "lucide-react";
import { injectUserStyles } from "../userStyles";

const BASE = process.env.REACT_APP_BACKEND_URL || "";

const STYLE_ID = "quotes-page-styles";
function injectLocal() {
  injectUserStyles();
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = STYLE_ID;
  el.textContent = `
    .qp-card {
      background:#fff; border:1px solid #e8ecf0; border-radius:10px;
      overflow:hidden; margin-bottom:16px;
    }
    .qp-card.ready { border-color:rgba(99,102,241,.3); }
    .qp-card-head {
      padding:14px 18px;
      background:#f9fafb; border-bottom:1px solid #f3f4f6;
    }
    .qp-card.ready .qp-card-head { background:rgba(99,102,241,.06); border-bottom-color:rgba(99,102,241,.1); }
    .qp-card-body { padding:16px 18px; }
    .qp-project-name { font-size:14px; font-weight:600; color:#0c1118; }
    .qp-project-date {
      display:flex; align-items:center; gap:5px;
      font-family:'DM Sans',sans-serif; font-size:10px; color:#9ca3af; margin-top:3px;
    }
    .qp-status-desc {
      display:flex; align-items:flex-start; gap:10px;
      padding:11px 14px; border-radius:7px; border:1px solid;
      font-size:13px; margin-bottom:12px;
    }
    .qp-download-btn {
      display:flex; align-items:center; justify-content:space-between;
      padding:12px 16px; background:#0c1118; border-radius:8px;
      color:#fff; text-decoration:none; font-size:13.5px; font-weight:600;
      transition:background 150ms; margin-bottom:12px;
    }
    .qp-download-btn:hover { background:#1e2832; }
    .qp-card-meta {
      display:flex; justify-content:space-between;
      font-family:'DM Sans',sans-serif; font-size:10px; color:#d1d5db;
    }
    .qp-tracker {
      display:flex; align-items:center; gap:0; margin-top:12px;
    }
    .qp-tracker-step { display:flex; flex-direction:column; align-items:center; }
    .qp-tracker-dot {
      width:24px; height:24px; border-radius:50%;
      display:flex; align-items:center; justify-content:center;
      font-size:11px; font-weight:700; border:2px solid;
    }
    .qp-tracker-dot.done   { background:#10b981; border-color:#10b981; color:#fff; }
    .qp-tracker-dot.active { background:#0c1118; border-color:#0c1118; color:#fff; }
    .qp-tracker-dot.idle   { background:#fff; border-color:#e2e8f0; color:#9ca3af; }
    .qp-tracker-label {
      font-family:'DM Sans',sans-serif; font-size:9px; letter-spacing:.06em;
      margin-top:4px; white-space:nowrap; text-align:center;
    }
    .qp-tracker-line { flex:1; height:2px; margin-bottom:16px; }
    .qp-filter-btn {
      padding:7px 14px; border-radius:7px; font-size:13px; font-weight:500;
      cursor:pointer; border:1px solid #e2e8f0; background:#fff;
      color:#6b7280; transition:all 150ms;
    }
    .qp-filter-btn.active { background:#0c1118; color:#fff; border-color:#0c1118; }
    .qp-filter-btn:hover:not(.active) { border-color:#d1d5db; color:#374151; }
    .qp-how-step {
      display:flex; gap:12px; padding:14px; border:1px solid #e8ecf0;
      border-radius:8px; background:#f9fafb;
    }
    .qp-how-num {
      width:28px; height:28px; border-radius:50%;
      background:#0c1118; color:#fff; font-size:11px; font-weight:700;
      display:flex; align-items:center; justify-content:center; flex-shrink:0;
    }
  `;
  document.head.appendChild(el);
}

/* ── Helpers ── */
async function jfetch(path, opts = {}) {
  const res = await fetch(`${BASE}${path}`, {
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    ...opts,
  });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json();
}

const STATUS_CONFIG = {
  draft:          { descBg: "#f1f5f9", descBorder: "#e2e8f0", descText: "#475569", icon: Clock,      label: "Draft",         description: "Your request is being prepared." },
  pending_review: { descBg: "rgba(249,171,20,.08)", descBorder: "rgba(249,171,20,.25)", descText: "#92400e", icon: Clock, label: "Under Review", description: "Our team is reviewing your request and will prepare a quote soon." },
  approved:       { descBg: "rgba(16,185,129,.08)", descBorder: "rgba(16,185,129,.2)", descText: "#065f46", icon: CheckCircle, label: "Approved", description: "Approved — a quote PDF is being prepared. You will be notified when it is ready." },
  rejected:       { descBg: "rgba(239,68,68,.07)", descBorder: "rgba(239,68,68,.2)", descText: "#991b1b", icon: XCircle, label: "Rejected", description: "Your request could not be fulfilled. Please contact support." },
  sent_to_client: { descBg: "rgba(99,102,241,.07)", descBorder: "rgba(99,102,241,.2)", descText: "#3730a3", icon: Send, label: "Quote Ready", description: "Your quote is ready! Download the PDF below." },
  accepted:       { descBg: "rgba(5,150,105,.07)", descBorder: "rgba(5,150,105,.2)", descText: "#064e3b", icon: ThumbsUp, label: "Accepted", description: "You have accepted this quote." },
  declined:       { descBg: "rgba(244,63,94,.07)", descBorder: "rgba(244,63,94,.2)", descText: "#9f1239", icon: ThumbsDown, label: "Declined", description: "You have declined this quote." },
};

function StatusBadge({ s }) {
  const cfg = STATUS_CONFIG[s] || { label: s, descBg: "#f3f4f6", descText: "#6b7280" };
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: "5px",
      padding: "3px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: 600,
      background: cfg.descBg, color: cfg.descText, border: `1px solid ${cfg.descBorder || "#e2e8f0"}`,
      fontFamily: "'DM Sans', sans-serif", letterSpacing: ".04em",
    }}>
      {cfg.label || String(s || "").replaceAll("_", " ")}
    </span>
  );
}

function StatusTracker({ status }) {
  const steps  = [
    { key: "pending_review", label: "Review" },
    { key: "approved",       label: "Preparing" },
    { key: "sent_to_client", label: "Ready" },
  ];
  const ORDER  = ["pending_review", "approved", "sent_to_client", "accepted"];
  const curIdx = ORDER.indexOf(status);
  if (["draft", "rejected", "declined"].includes(status)) return null;
  return (
    <div className="qp-tracker">
      {steps.map((step, i) => {
        const stepIdx = ORDER.indexOf(step.key);
        const done    = stepIdx < curIdx;
        const active  = stepIdx === curIdx || (status === "accepted" && step.key === "sent_to_client");
        return (
          <React.Fragment key={step.key}>
            <div className="qp-tracker-step">
              <div className={`qp-tracker-dot ${done ? "done" : active ? "active" : "idle"}`}>
                {done ? "✓" : i + 1}
              </div>
              <div className="qp-tracker-label" style={{ color: active ? "#0c1118" : done ? "#10b981" : "#9ca3af" }}>
                {step.label}
              </div>
            </div>
            {i < steps.length - 1 && (
              <div className="qp-tracker-line" style={{ background: done ? "#10b981" : "#e2e8f0" }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function QuoteCard({ q }) {
  const cfg     = STATUS_CONFIG[q.status] || STATUS_CONFIG.draft;
  const Icon    = cfg.icon;
  const isReady = q.status === "sent_to_client" || q.status === "accepted";
  return (
    <div className={`qp-card ${isReady ? "ready" : ""}`}>
      <div className="qp-card-head">
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 8, flexShrink: 0,
              background: isReady ? "#0c1118" : "#f3f4f6",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <Building size={17} style={{ color: isReady ? "#f9ab14" : "#9ca3af" }} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div className="qp-project-name">{q.project?.name || q.projectId}</div>
              <div className="qp-project-date">
                <Calendar size={10} />
                Submitted {new Date(q.createdAt).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}
              </div>
            </div>
          </div>
          <StatusBadge s={q.status} />
        </div>
        <StatusTracker status={q.status} />
      </div>

      <div className="qp-card-body">
        <div className="qp-status-desc" style={{ background: cfg.descBg, borderColor: cfg.descBorder || "#e2e8f0" }}>
          <Icon size={15} style={{ color: cfg.descText, flexShrink: 0, marginTop: 1 }} />
          <span style={{ fontSize: "12.5px", color: cfg.descText }}>{cfg.description}</span>
        </div>

        {isReady && q.fileUrl && (
          <a href={q.fileUrl} target="_blank" rel="noreferrer" className="qp-download-btn">
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Download size={15} /> Download Your Quote PDF
            </div>
            <ExternalLink size={14} style={{ opacity: .6 }} />
          </a>
        )}

        {isReady && !q.fileUrl && (
          <div style={{
            display: "flex", alignItems: "center", gap: "8px", padding: "11px 14px",
            background: "rgba(99,102,241,.07)", border: "1px solid rgba(99,102,241,.2)",
            borderRadius: "7px", fontSize: "12.5px", color: "#3730a3", marginBottom: "12px",
          }}>
            <RefreshCw size={13} style={{ animation: "upSpin .7s linear infinite" }} />
            Quote file is being prepared. Check back shortly.
          </div>
        )}

        <div className="qp-card-meta">
          <span>ID: {q.id.slice(0, 16)}…</span>
          <span>Updated {new Date(q.updatedAt).toLocaleDateString()}</span>
        </div>
      </div>
    </div>
  );
}

const FILTER_TABS = [
  { value: "all",            label: "All" },
  { value: "pending_review", label: "In Review" },
  { value: "sent_to_client", label: "Ready" },
];

export default function QuotesPage() {
  const [items, setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => { injectLocal(); }, []);

  async function load() {
    setLoading(true); setError(null);
    try {
      const r = await jfetch("/api/quotes/my");
      const poolQuotes = (r.items || []).filter((q) => q.destination === "solo_user" || !q.orgId);
      setItems(poolQuotes);
    } catch {
      setError("Failed to load quotes. Please try refreshing.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    if (filter === "sent_to_client") return items.filter((q) => q.status === "sent_to_client" || q.status === "accepted");
    return items.filter((q) => q.status === filter);
  }, [items, filter]);

  const counts = useMemo(() => ({
    inReview: items.filter((q) => q.status === "pending_review").length,
    ready:    items.filter((q) => q.status === "sent_to_client" || q.status === "accepted").length,
  }), [items]);

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif", maxWidth: 860, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", marginBottom: "24px" }}>
        <div>
          <div className="up-title">My Quotes</div>
          <div className="up-sub">Track your quote requests and download PDFs when ready</div>
        </div>
        <button className="up-btn up-btn-outline" onClick={load} disabled={loading}>
          <RefreshCw size={14} style={{ animation: loading ? "upSpin .7s linear infinite" : "none" }} />
          Refresh
        </button>
      </div>

      {/* KPI row */}
      {items.length > 0 && (
        <div className="up-kpi-grid" style={{ gridTemplateColumns: "repeat(3,1fr)", marginBottom: "16px" }}>
          {[
            { accent: "#0c1118", val: items.length,    lbl: "Total Requests" },
            { accent: "#f9ab14", val: counts.inReview, lbl: "Under Review"   },
            { accent: "#6366f1", val: counts.ready,    lbl: "Quotes Ready"   },
          ].map((k) => (
            <div className="up-kpi" key={k.lbl}>
              <div className="up-kpi-accent" style={{ background: k.accent }} />
              <div className="up-kpi-val">{k.val}</div>
              <div className="up-kpi-lbl">{k.lbl}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filter tabs */}
      {items.length > 0 && (
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "20px" }}>
          {FILTER_TABS.map(({ value, label }) => (
            <button key={value} onClick={() => setFilter(value)} className={`qp-filter-btn ${filter === value ? "active" : ""}`}>
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "12px 16px", background: "rgba(239,68,68,.07)", border: "1px solid rgba(239,68,68,.2)", borderRadius: "8px", fontSize: "13px", color: "#991b1b", marginBottom: "16px" }}>
          <AlertCircle size={15} style={{ flexShrink: 0 }} /> {error}
        </div>
      )}

      {/* Loading */}
      {loading && <div className="up-loading"><div className="up-spinner" /> Loading your quotes…</div>}

      {/* Empty — no quotes */}
      {!loading && !error && items.length === 0 && (
        <div className="up-panel">
          <div style={{ padding: "40px 24px", textAlign: "center" }}>
            <div style={{ width: 56, height: 56, borderRadius: "50%", background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
              <FileText size={24} style={{ color: "#9ca3af" }} />
            </div>
            <div style={{ fontSize: "16px", fontWeight: 600, color: "#0c1118", marginBottom: "6px" }}>No Quote Requests Yet</div>
            <div style={{ fontSize: "13px", color: "#9ca3af", maxWidth: 360, margin: "0 auto 24px" }}>
              Request a quote from within the 3D editor on any of your projects.
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px,1fr))", gap: "12px", maxWidth: 680, margin: "0 auto" }}>
              {[
                { step: "1", title: "Open a project",    desc: "Go to Projects and open any project in the 3D editor." },
                { step: "2", title: "Request a quote",   desc: 'Click "Request Quote" in the sidebar and fill out your contact details.' },
                { step: "3", title: "Download your PDF", desc: "Once prepared, a download button appears here and you'll get an email." },
              ].map(({ step, title, desc }) => (
                <div className="qp-how-step" key={step}>
                  <div className="qp-how-num">{step}</div>
                  <div>
                    <div style={{ fontSize: "13px", fontWeight: 600, color: "#0c1118" }}>{title}</div>
                    <div style={{ fontSize: "11.5px", color: "#9ca3af", marginTop: "3px" }}>{desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Quote cards */}
      {!loading && filtered.length > 0 && filtered.map((q) => <QuoteCard key={q.id} q={q} />)}

      {/* Empty filtered */}
      {!loading && items.length > 0 && filtered.length === 0 && (
        <div className="up-panel">
          <div className="up-empty">
            <Info size={28} style={{ color: "#d1d5db", margin: "0 auto" }} />
            <div className="up-empty-sub">No quotes with this status.</div>
          </div>
        </div>
      )}

      {/* Info note */}
      {!loading && items.length > 0 && (
        <div style={{ display: "flex", alignItems: "flex-start", gap: "10px", padding: "14px 16px", background: "#f9fafb", border: "1px solid #e8ecf0", borderRadius: "8px", fontSize: "12.5px", color: "#6b7280", marginTop: "16px" }}>
          <Info size={13} style={{ color: "#9ca3af", flexShrink: 0, marginTop: 2 }} />
          Quote PDFs are prepared manually by our team. You will receive an email and in-app notification as soon as your quote is ready.
        </div>
      )}
    </div>
  );
}
